import { useState, useEffect, useCallback, useRef } from 'react';
import { getCompanyUsers } from '../api';

export default function useUsers({ companyId }) {
    const emptyData = { rows: [], total: 0, page: 1, pageSize: 20, totalPages: 0 };
    const [data, setData] = useState(emptyData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [search, setSearch] = useState('');
    const [departmentId, setDepartmentId] = useState(undefined);
    const [sort, setSort] = useState('name');
    const [order, setOrder] = useState('asc');
    const fetchIdRef = useRef(0);

    const fetch = useCallback(async () => {
        if (!companyId) return;
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const params = { page, pageSize, search: search || undefined, departmentId, sort, order };
            const res = await getCompanyUsers(companyId, params);
            if (fetchId === fetchIdRef.current) {
                setData(res.data || emptyData);
            }
        } catch (err) {
            if (fetchId === fetchIdRef.current) {
                setError(err.response?.data?.message || err.message || 'Failed to load users');
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    }, [companyId, page, pageSize, search, departmentId, sort, order]);

    useEffect(() => { fetch(); }, [fetch]);

    // Reset page to 1 when company, search, filter, or sort changes
    useEffect(() => { setPage(1); }, [companyId, search, departmentId, sort, order]);

    const handleSetSearch = useCallback((val) => {
        setSearch(val);
    }, []);

    const handleSetDepartmentId = useCallback((val) => {
        setDepartmentId(val);
    }, []);

    return {
        data,
        loading,
        error,
        page,
        setPage,
        sort,
        order,
        setSort,
        setOrder,
        setSearch: handleSetSearch,
        setDepartmentId: handleSetDepartmentId,
        refetch: fetch,
    };
}
